export type CreateSoloVideoRecordingStreamOptions = {
    inputStream: MediaStream;
    audioStream?: MediaStream;
    disableAudio?: boolean;
};

/**
 * Creates the recorder-owned branch of an existing capture stream.
 *
 * The preview and recorder must not consume the same MediaStreamTrack object.
 * Cloning keeps a single underlying camera/microphone capture while giving each
 * consumer an independent track lifecycle.
 */
export function createSoloVideoRecordingStream({
    inputStream,
    audioStream,
    disableAudio = false,
}: CreateSoloVideoRecordingStreamOptions): MediaStream {
    if (!inputStream || typeof inputStream.getVideoTracks !== 'function') {
        throw new Error('A live preview stream is required for solo video recording.');
    }

    const sourceVideoTrack = inputStream
        .getVideoTracks()
        .find((track) => track.readyState !== 'ended');
    if (!sourceVideoTrack) {
        throw new Error('The preview stream has no live video track.');
    }

    const clonedTracks: MediaStreamTrack[] = [];
    try {
        clonedTracks.push(sourceVideoTrack.clone());

        if (!disableAudio) {
            const sourceAudioTrack = (audioStream ?? inputStream)
                .getAudioTracks()
                .find((track) => track.readyState !== 'ended');
            if (sourceAudioTrack) {
                clonedTracks.push(sourceAudioTrack.clone());
            }
        }

        return new MediaStream(clonedTracks);
    } catch (error) {
        clonedTracks.forEach((track) => {
            try { track.stop(); } catch { }
        });
        throw error;
    }
}
